// @ts-strict-ignore
import {
  useCallback, useContext, useState,
} from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Grid,
  Icon,
  IconName,
  Label,
  MultiSelect,
  Text,
  TextArea,
  useSnackBar,
} from '@usealloy/component-library';
import type { Option } from '@usealloy/component-library';
import {
  formatFileSize, handleFileChange, handleUploadDocument, updateFiles,
} from './documentHelper';
import { UserContext } from '~/dashboard/context';
import find from 'lodash/find';
import {
  ChooseFileInput,
  ChooseFileLabel,
  CloudUploadIconWrapper,
  FileNameWrapper,
  FileRowGrid,
  TargetDropBox,
  TruncatedFileNameText,
  UploadInfo,
} from '~/components/inputs/ChooseFileInputStyles';
import type { Entity } from '~/entities/types';

type EntityForUpload = Pick<Entity, 'entity_name' | 'entity_token'>;

/**
 * Base props shared across all document upload components (Modal, Popover, Content).
 * These represent the core data needed for document upload functionality.
 */
export interface BaseDocumentUploadProps {
  caseToken?: string;
  documentTypes: Array<{
    document_type_id: number;
    type: string;
  }>;
  entities: {
    [entity_token: string]: EntityForUpload;
  };
  evaluationToken?: string;
  investigationToken?: string;
}

/**
 * Props specific to DocumentUploadContent component.
 * Extends BaseDocumentUploadProps with presentation and callback props.
 */
export interface DocumentUploadContentProps extends BaseDocumentUploadProps {
  onUploadComplete: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  showUploadButton?: boolean;
  title?: string;
  showDescription?: boolean;
}

/**
 * Core document upload functionality extracted for reuse in both Modal and Popover contexts.
 * Handles drag-and-drop, file selection, entity assignment, and upload submission.
 */
export const DocumentUploadContent = ({
  caseToken,
  documentTypes,
  entities,
  evaluationToken,
  investigationToken,
  onUploadComplete,
  onCancel,
  showCancelButton = true,
  showUploadButton = true,
  title,
  showDescription = true,
}: DocumentUploadContentProps) => {
  const { agent, typhonDomain } = useContext(UserContext);
  const { isEmployee } = agent || {};
  const [uploading, setUploading] = useState(false);
  const [isCaseEvidence, setIsCaseEvidence] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const defaultFileType = find(documentTypes, { type: 'other' });

  const isMultiEntityContext = Object.values(entities).length > 1;

  // For single-entity context, get the entity to use for all files
  const singleEntity = !isMultiEntityContext ? Object.values(entities)[0] : null;

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleReset = () => {
    setFiles([]);
    setIsCaseEvidence(false);
  };

  const handleFileDrop = item => {
    setIsDragging(false);
    if (item) {
      const uploaded = item.files;
      updateFiles(defaultFileType, uploaded, setFiles);
    }
  };

  const [, drop] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    drop(item) {
      handleFileDrop(item);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [handleFileDrop]);

  const addSnackBar = useSnackBar();

  const onSubmit = useCallback(
    event => {
      const caseMetaData = isCaseEvidence && { case_token: caseToken };

      // Determine which entity name to show in success message
      const entityForMessage = isMultiEntityContext
        ? null // Will show generic message for multi-entity uploads
        : singleEntity;

      const successHandler = () => {
        handleReset(); // Reset files after successful upload
        const successMessage = entityForMessage
          ? `Documents successfully uploaded to ${entityForMessage?.entity_name || entityForMessage?.entity_token}`
          : 'Documents successfully uploaded';
        addSnackBar({
          title: successMessage,
          type: 'success',
        });
        onUploadComplete();
      };

      handleUploadDocument({
        addSnackBar,
        agentEmail: agent?.email,
        caseMetaData,
        documentTypes,
        entity: singleEntity, // For single-entity context, pass the entity
        evaluationToken,
        event,
        files,
        investigationToken,
        isMultiEntityContext,
        onClose: onCancel || (() => {}),
        setUploading,
        successHandler,
        typhonDomain,
      });
    }, [
      addSnackBar,
      agent?.email,
      caseToken,
      documentTypes,
      singleEntity,
      evaluationToken,
      files,
      investigationToken,
      isCaseEvidence,
      isMultiEntityContext,
      onCancel,
      onUploadComplete,
      typhonDomain,
    ]);

  const handleDeleteFile = index => {
    setFiles(currentFiles => {
      const newFiles = [...currentFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  // Check if all files have entities selected (for multi-entity context)
  const allFilesHaveEntity = isMultiEntityContext
    ? files.every(file => file.entity?.entity_token)
    : true;

  return (
    <Box direction="column" alignItems="stretch" gap={4} style={{ width: '100%' }}>
      {title && (
        <Text size={4} weight={500}>
          {title}
        </Text>
      )}
      {showDescription && (
        <Text>
          {`Upload your documents below to add them to this ${investigationToken ? 'Investigation' : 'Entity'}`}
        </Text>
      )}
      <TargetDropBox
        className={isDragging ? 'drag-over' : ''}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        ref={drop}
        direction="column"
        gap={3}
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        <CloudUploadIconWrapper>
          <Icon icon={IconName.CloudUpload} />
        </CloudUploadIconWrapper>
        <Text lineHeight={4}>
          {'Drop your file here, or '}
          <ChooseFileLabel htmlFor="document-upload">
            browse files
          </ChooseFileLabel>
        </Text>
        <UploadInfo>
          {'Max File Size: '}
          <UploadInfo weight={700}>50MB</UploadInfo>
        </UploadInfo>
        <UploadInfo>
          {'Supported file types: '}
          <UploadInfo weight={700}>JPG, PNG, PDF, HEIF, MSG</UploadInfo>
        </UploadInfo>
        <ChooseFileInput
          id="document-upload"
          className="file"
          type="file"
          multiple
          name="document_upload"
          formEncType="multipart/form-data"
          onChange={event => handleFileChange(defaultFileType, event, setFiles)}
        />
      </TargetDropBox>
      {caseToken && (
        <Checkbox
          label="These files are evidence"
          checked={isCaseEvidence}
          onChange={event => { setIsCaseEvidence(event.target.checked); }}
          disabled={uploading || files.length === 0}
        />
      )}
      {files.map((file, index) => (
        <FileRowGrid
          key={`file-${file.fileObj.name}-${file.fileObj.lastModified}-${file.fileObj.size}`}
          colGap={4}
          style={{ minWidth: '100%' }}
        >
          <Grid.Item columns={{ sm: 6, md: 6, lg: 6 }}>
            <Box direction="column" alignItems="flex-start" gap={2}>
              <Label>Uploaded</Label>
              <FileNameWrapper justifyContent="flex-start">
                <Box justifyContent="space-between" gap={0}>
                  <Box gap={0} justifyContent="flex-start" style={{ maxWidth: 'calc(100% - var(--cl-space-10))' }}>
                    <TruncatedFileNameText>
                      {file.fileObj.name.slice(0, -8)}
                    </TruncatedFileNameText>
                    <Text>{file.fileObj.name.slice(-8)}</Text>
                    <Text style={{ color: 'var(--cl-gray-700)' }}>
                      &nbsp;{`• ${formatFileSize(file.fileObj.size)}`}
                    </Text>
                  </Box>
                  <Button
                    icon={IconName.Trash}
                    onClick={() => { handleDeleteFile(index); }}
                    size={ButtonSize.small}
                    variant={ButtonVariant.tertiary}
                  />
                </Box>
              </FileNameWrapper>
            </Box>
          </Grid.Item>
          <Grid.Item columns={{ sm: 6, md: 6, lg: 6 }}>
            <MultiSelect
              defaultValue={documentTypes.filter(({ type }) => type === 'other').map(type => ({
                label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                value: type.document_type_id,
              }))}
              isMulti={false}
              label="Type"
              onChange={({ value }: Option) => {
                setFiles(currentFiles => {
                  const newFiles = [...currentFiles];
                  newFiles.splice(index, 1, {
                    ...currentFiles[index],
                    type: (parseInt(value, 10)),
                  });
                  return newFiles;
                });
              }}
              options={documentTypes.map(type => ({
                label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                value: String(type.document_type_id),
              }))}
              selectedOptions={(
                documentTypes
                  .filter(({ document_type_id }) => document_type_id === file.type)
                  .map(type => ({
                    label: `${type.type.charAt(0).toUpperCase()}${type.type.slice(1)}`.replaceAll('_', ' '),
                    value: String(type.document_type_id),
                  }))
              )}
            />
          </Grid.Item>
          {isMultiEntityContext ? (
            <>
              <Grid.Item columns={{ sm: 12, md: 12, lg: 12 }}>
                <MultiSelect
                  isClearable={false}
                  isMulti={false}
                  label="Entity"
                  options={Object.values(entities).map(ent => ({
                    label: ent.entity_name || ent.entity_token,
                    value: ent.entity_token,
                  }))}
                  placeholder="Select an entity"
                  selectedOptions={file.entity?.entity_token
                    ? [{
                      label: file.entity.entity_name || file.entity.entity_token,
                      value: file.entity.entity_token,
                    }]
                    : []}
                  onChange={(selected: Option) => {
                    setFiles(currentFiles => {
                      const newFiles = [...currentFiles];
                      newFiles.splice(index, 1, {
                        ...currentFiles[index],
                        entity: entities[selected.value],
                      });
                      return newFiles;
                    });
                  }}
                />
              </Grid.Item>
              <Grid.Item columns={{ sm: 12, md: 12, lg: 12 }}>
                <TextArea
                  disabled={isEmployee}
                  label="Note"
                  onChange={({ target: { value } }) => {
                    setFiles(currentFiles => {
                      const newFiles = [...currentFiles];
                      newFiles.splice(index, 1, {
                        ...currentFiles[index],
                        notes: value,
                      });
                      return newFiles;
                    });
                  }}
                  placeholder={isEmployee ? 'Notes are disabled for employees' : ''}
                  rows={1}
                  style={{
                    resize: 'vertical',
                    height: 'var(--cl-space-10)',
                    minHeight: 'var(--cl-space-10)',
                  }}
                  value={file.notes}
                />
              </Grid.Item>
            </>
          ) : (
            <Grid.Item columns={{ sm: 12, md: 12, lg: 12 }}>
              <TextArea
                disabled={isEmployee}
                label="Note"
                onChange={({ target: { value } }) => {
                  setFiles(currentFiles => {
                    const newFiles = [...currentFiles];
                    newFiles.splice(index, 1, {
                      ...currentFiles[index],
                      notes: value,
                    });
                    return newFiles;
                  });
                }}
                placeholder={isEmployee ? 'Notes are disabled for employees' : ''}
                rows={1}
                style={{
                  resize: 'vertical',
                  height: 'var(--cl-space-10)',
                  minHeight: 'var(--cl-space-10)',
                }}
                value={file.notes}
              />
            </Grid.Item>
          )}
        </FileRowGrid>
      ))}
      {(showUploadButton || showCancelButton) && (
        <Box justifyContent="flex-end" gap={2} style={{ width: '100%' }}>
          {showCancelButton && onCancel && (
            <Button onClick={onCancel}>Cancel</Button>
          )}
          {showUploadButton && (
            <Button
              onClick={onSubmit}
              variant={ButtonVariant.primary}
              loading={uploading}
              disabled={files.length === 0 || !allFilesHaveEntity}
            >
              Upload
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
